import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { BlockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import type { RemixModalMetadata } from '../types';

export type OpenRemixModalOptions = {
	metadata?: RemixModalMetadata;
	nodePos?: number;
	nodeType?: string;
	source?: string;
};

/**
 * Opens the remix modal and, if a node position and type are provided, updates
 * the editor selection to reflect the clicked node using `expandAndUpdateSelection`.
 * Uses `tr.selection` as the current selection at dispatch time.
 */
export const makeOpenRemixModal =
	(api: ExtractInjectionAPI<BlockControlsPlugin> | undefined) =>
	({ nodePos, nodeType, metadata, source }: OpenRemixModalOptions = {}): EditorCommand =>
	({ tr }) => {
		if (nodePos !== undefined && nodeType) {
			api?.blockControls?.commands
				.expandAndUpdateSelection({
					startPos: nodePos,
					nodeType,
					selection: tr.selection,
					isShiftPressed: false,
				})({ tr });

			// If a multi-block TextSelection was set, signal to the selection plugin
			// so that ak-editor-selected-node decorations are applied to all selected nodes.
			if (tr.selection instanceof TextSelection) {
				api?.selection?.commands?.setBlockSelection(tr.selection)({ tr });
			}
		}
		tr.setMeta(malleableUiPluginKey, {
			isRemixModalOpen: true,
			remixModalMetadata: metadata,
			remixSource: source,
		});
		return tr;
	};
